import * as React from "react";
import { Alert, Button, Dropdown, Modal, notification, Switch, Tag, Tooltip } from "antd";
import { ChevronDown, RefreshCw } from "react-feather";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { linksOf, request } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { updateLastDeploymentTimeOnHubspot } from "../../../services/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { CommandModal_StartCronJob } from "../commands/commandModal_startCronJob";
import { CommandModal_ReplicaTermination } from "../commands/commandModal_replicaTermination";
import { ItemType, MenuItemType } from "antd/es/menu/interface";
import { formatStrings } from "../../../utils/time";
import { Deployment } from "../../../schema/types/workload/deployment";
import { itemHelpers as itemHelpersBase } from "../../../mst/kinds/item.helpers";
import { workloadHelpers } from "../../../mst/kinds/workload.helpers";
import { WorkloadCloneModal } from "../cloneModal";
import { Workload } from "../../../schema/types/workload/entity";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { dateString } from "../../../components/dateStringFn";
import { DateString } from "../../../components/dateString";
import { ViewModal } from "../../../components/modals/viewModal";
import { DeleteModal } from "../../../components/modals/deleteModal";
import { TerraformImportModal } from "../../../components/modals/tfImportModal";
import { K8sCrdExportModal } from "../../../components/modals/k8sCrdExportModal";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { selectContext } from "../../../store/contextSlice";
import { selectLabel } from "../../../store/timezoneSlice";
import { Kind } from "../../../schema/types/base";
import { useGetWorkloadQuery } from "../../../store/api/workloadApi";
import { LastUpdated } from "../../../components/LastUpdated";
import { applyActions } from "../../../store/applySlice";

interface Props {
  workload: Workload;
  deployments: Deployment[];
}
export const WorkloadDetailHeader: React.FC<Props> = ({ workload, deployments }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const selfLink = linksOf(workload).self!;
  const { refetch, isFetching, fulfilledTimeStamp } = useGetWorkloadQuery(selfLink, { skip: !selfLink });

  const dispatch = useAppDispatch();
  const { org } = useAppSelector(selectContext);
  const timezoneLabel = useAppSelector(selectLabel);
  const terraformSupportedKinds = useAppSelector((state) => state.rest.terraformSupportedKinds);
  const k8sCrdSupportedKinds = useAppSelector((state) => state.rest.k8sCrdSupportedKinds);

  const [isProcessingAction, setIsProcessingAction] = React.useState(false);

  const [timeKey, setTimeKey] = React.useState(Date.now());
  const [terraformImportCommand, setTerraformImportCommand] = React.useState("");
  const [action, setAction] = React.useState<
    | "edit"
    | "view"
    | "clone"
    | "suspend"
    | "unsuspend"
    | "delete"
    | "replicaTermination"
    | "startCronJob"
    | "forceRedeploy"
    | "lock"
    | "tfImportCommand"
    | "k8sCrdExport"
    | ""
  >("");

  const kind: any = workload.kind;

  const itemHelpers = itemHelpersBase({ org: org, gvc: parseLink(selfLink, { useInputCtx: true }).gvc || "", kind: kind, name: workload.name });

  function getIsLocked(): boolean {
    return (workload.tags || {})["cpln/protected"] == "true";
  }

  const onLock = async () => {
    try {
      const isLocked = getIsLocked();
      await request({ method: "patch", body: { tags: { "cpln/protected": !isLocked } }, url: selfLink });
      notification.success({
        message: "Success",
        description: `Object was ${isLocked ? "unlocked" : "locked"} successfully`,
      });
      refetch();
      setAction("");
    } catch (e) {
      setAction("");
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  };

  const url = workload.spec.type !== "cron" && (workload.status?.canonicalEndpoint || workload.status?.endpoint);
  const [isWorkloadSuspended, setIsWorkloadSuspended] = React.useState<boolean>(null as any);

  const canExportToTerraform = terraformSupportedKinds.includes(kind as Kind);
  const canExportToK8sCrd = k8sCrdSupportedKinds.includes(kind as Kind);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeKey(Date.now());
    }, 1 * 60 * 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  React.useEffect(() => {
    checkWorkloadSuspended();
  }, [workload.version]);

  React.useEffect(() => {
    if (terraformImportCommand) {
      setAction("tfImportCommand");
    }
  }, [terraformImportCommand]);

  async function checkWorkloadSuspended() {
    const value = await workloadHelpers.getAsyncIsSuspended(selfLink);
    setIsWorkloadSuspended(value);
  }

  async function handleActionsClick({ key }: any) {
    switch (key) {
      case "suspend":
        if (isWorkloadSuspended) {
          setAction("unsuspend");
        } else {
          setAction("suspend");
        }
        break;
      case "force-redeployment":
        setAction("forceRedeploy");
        break;
      case "start-cron-job":
        setAction("startCronJob");
        break;
      case "edit":
        editApply();
        break;
      case "view":
        setAction("view");
        break;
      case "export-json":
        await itemHelpers.handleExportJSON([selfLink], false);
        break;
      case "export-yaml":
        await itemHelpers.handleExportYaml([selfLink], false);
        break;
      case "export-json-slim":
        await itemHelpers.handleExportJSON([selfLink], true);
        break;
      case "export-yaml-slim":
        await itemHelpers.handleExportYaml([selfLink], true);
        break;
      case "export-terraform":
        setTerraformImportCommand(await itemHelpers.handleExportTerraform([selfLink]));
        break;
      case "export-k8s-crd":
        setAction("k8sCrdExport");
        break;
      case "replica-termination":
        setAction("replicaTermination");
        break;
      case "clone":
        setAction("clone");
        break;
      case "delete":
        setAction("delete");
        break;
      default:
        break;
    }
  }

  async function editApply() {
    dispatch(applyActions.initialize({ itemLink: selfLink, previousPath: pathname }));
    navigate(`/console/apply`);
  }

  const items: ItemType<MenuItemType>[] = [];
  items.push(
    //
    { key: "suspend", label: isWorkloadSuspended ? "Unsuspend" : "Suspend" },
    { key: "force-redeployment", label: `Force Redeployment` },
  );
  if (workload.spec.type === "cron") {
    items.push({ key: "start-cron-job", label: `Start Cron Job` });
  }
  items.push({
    key: "replica-termination",
    label: `${workload.spec.type === "stateful" ? "Restart" : "Stop"} Replica`,
  });
  items.push({ type: "divider" });
  items.push({ key: "edit", label: "Edit & Apply" });
  items.push({ key: "view", label: "View" });
  const exportItem = {
    key: "ignore2",
    label: "Export",
    children: [
      { key: "export-json", label: "JSON" },
      { key: "export-yaml", label: "YAML" },
      { key: "export-json-slim", label: "JSON Slim" },
      { key: "export-yaml-slim", label: "YAML Slim" },
    ],
  };
  if (canExportToTerraform) {
    exportItem.children.push({ key: "export-terraform", label: "Terraform" });
  }
  if (canExportToK8sCrd) {
    exportItem.children.push({ key: "export-k8s-crd", label: "K8s CRD" });
  }
  items.push(exportItem);
  items.push({ key: "clone", label: "Clone" });
  items.push({ key: "delete", label: "Delete", danger: true });

  let showByokMessage = false;

  const messageToCheck = "is disabled on BYOK clusters".toLowerCase();
  for (const deployment of deployments) {
    if (deployment.status?.message?.toLowerCase().includes(messageToCheck)) {
      showByokMessage = true;
    }
    for (const version of deployment.status?.versions || []) {
      if (version.message?.toLowerCase().includes(messageToCheck)) {
        showByokMessage = true;
      }
    }
  }

  function viewModalOnClose() {
    setAction("");
  }

  return (
    <>
      <div className="n-detail-header flex items-center justify-between pb-4 h-20 z-10">
        <div>
          <div data-testid="detail-kind" className="text-dark text-xs">
            {`${workload.spec.type} Workload`.toUpperCase()} | GVC {parseLink(selfLink, { useInputCtx: true }).gvc}
          </div>
          <div className="flex items-center">
            <div data-testid="detail-name" className="text-2xl font-semibold leading-none">
              {workload.name}
            </div>
            {!!url && workload.spec.type !== "cron" ? <ExternalLink className="ml-4" url={url} label="Open" /> : null}
            {workloadHelpers.getSuspendType(workload) != "notSuspended" ? (
              <Tooltip
                title={
                  <div style={{ width: "content-fit" }}>
                    <div>
                      A workload is suspended or partially suspended depending on the defaultOptions and localOptions
                      configuration.
                    </div>
                    <div>
                      Properties that decide this label are either "suspend=true", or "minScale=0" and "maxScale=0" at
                      the same time.
                    </div>
                  </div>
                }
              >
                <Tag className="ml-2 w-fit" color="error">
                  {workloadHelpers.getSuspendType(workload) === "suspended" ? "Suspended" : "Partially Suspended"}
                </Tag>
              </Tooltip>
            ) : null}
            {showByokMessage ? (
              <Alert
                className="ml-4"
                style={{ width: "fit-content", minWidth: "fit-content", maxWidth: "fit-content" }}
                type={"info"}
                message={`The canonical endpoint is disabled on BYOK clusters.`}
              />
            ) : null}
          </div>
          <LastUpdated prefix="Fetched" timestamp={fulfilledTimeStamp} />
        </div>
        <div className="flex-grow" />

        <div className="flex items-center gap-2">
          <div>{`Version ${workload.version}`}</div>
          <Button
            disabled={isFetching}
            loading={isFetching}
            onClick={() => refetch()}
            icon={<RefreshCw size={17} />}
            size="small"
            type="default"
          />
          {items.length > 0 ? (
            <Dropdown trigger={["click"]} menu={{ items: items, className: "menu", onClick: handleActionsClick }}>
              <Button type={"primary"} size="small" icon={<ChevronDown />} iconPosition="end">
                Actions
              </Button>
            </Dropdown>
          ) : null}
          <>
            <Tooltip
              title={[
                `Created: ${dateString(workload.created, { format: formatStrings.humanizeWAgo })}`,
                `Created (${timezoneLabel}): ${dateString(workload.created, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Created (UTC): ${dateString(workload.created, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
                `Last Modified: ${dateString(workload.lastModified, { format: formatStrings.humanizeWAgo })}`,
                `Last Modified (${timezoneLabel}): ${dateString(workload.lastModified, {
                  format: formatStrings.log,
                })}`,
                timezoneLabel !== "UTC"
                  ? `Last Modified (UTC): ${dateString(workload.lastModified, {
                      format: formatStrings.log,
                      utc: true,
                    })}`
                  : "",
              ]}
            >
              <div key={timeKey} className="ml-2">
                <DateString prefix={"Updated"} iso={workload.lastModified} format={formatStrings.humanizeWAgo} />
              </div>
            </Tooltip>
            <div>
              <Tooltip title={`Click to ${getIsLocked() ? "unlock" : "lock"} the object`} trigger={["hover"]}>
                <Switch
                  value={getIsLocked()}
                  aria-label="Toggle Lock"
                  onChange={() => setAction("lock")}
                  checkedChildren={<LockOutlined />}
                  unCheckedChildren={<UnlockOutlined />}
                />
              </Tooltip>
            </div>
          </>
        </div>
      </div>
      {action === "view" ? (
        <ViewModal
          kind={kind}
          link={selfLink}
          visible={true}
          onClose={viewModalOnClose}
          title={workload.name}
          filename={itemHelpers.getFileName()}
        />
      ) : action === "clone" ? (
        <WorkloadCloneModal workload={workload} onClose={() => setAction("")} />
      ) : action === "replicaTermination" ? (
        <CommandModal_ReplicaTermination
          onClose={() => setAction("")}
          onDone={() => setAction("")}
          visible={action === "replicaTermination"}
          workload={workload}
        />
      ) : action === "delete" ? (
        <DeleteModal
          kind={kind}
          itemName={workload.name}
          links={[selfLink]}
          onCancel={() => setAction("")}
          onComplete={() => setAction("")}
        />
      ) : action === "startCronJob" ? (
        <CommandModal_StartCronJob
          workload={workload}
          onDone={() => setAction("")}
          onClose={() => setAction("")}
          visible={true}
        />
      ) : action === "lock" ? (
        <Modal
          title={"Warning!"}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={onLock}
          cancelButtonProps={{ disabled: isFetching }}
          okButtonProps={{ disabled: isFetching, loading: isFetching }}
        >
          <div>
            {getIsLocked()
              ? "Unlocking the object will allow deletion. Are you sure?"
              : "Locking the object will prevent deletion. Are you sure?"}
          </div>
        </Modal>
      ) : action === "suspend" ? (
        <Modal
          title={"Are you sure you want to suspend this workload?"}
          width={620}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={async () => {
            setIsProcessingAction(true);
            const result = await workloadHelpers.handleSuspend([selfLink]);
            setIsProcessingAction(false);
            if (result === true) {
              notification.success({
                message: "Success",
                description: "Suspended workload",
              });
              refetch();
              setAction("");
            } else {
              setAction("");
              notification.warning({
                message: "Failed",
                description: result,
              });
            }
          }}
          cancelText={`Cancel`}
          okText={`Confirm`}
          cancelButtonProps={{ disabled: isFetching || isProcessingAction, type: "default", danger: true }}
          okButtonProps={{
            disabled: isFetching || isProcessingAction,
            loading: isFetching || isProcessingAction,
            type: "primary",
          }}
        >
          <div>
            <p>Suspending the workload will enable the "suspend" property.</p>
          </div>
        </Modal>
      ) : action === "unsuspend" ? (
        <Modal
          title={"Are you sure you want to unsuspend this workload?"}
          width={620}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={async () => {
            setIsProcessingAction(true);
            const result = await workloadHelpers.handleUnsuspend([selfLink]);
            setIsProcessingAction(false);
            if (result === true) {
              notification.success({
                message: "Success",
                description: "Unsuspended workload",
              });
              refetch();
              setAction("");
            } else {
              setAction("");
              notification.warning({
                message: "Failed",
                description: result,
              });
            }
          }}
          cancelText={`Cancel`}
          okText={`Unsuspend Workload`}
          cancelButtonProps={{ type: "default", danger: true, disabled: isFetching || isProcessingAction }}
          okButtonProps={{
            type: "primary",
            disabled: isFetching || isProcessingAction,
            loading: isFetching || isProcessingAction,
          }}
        >
          <div>
            <p>Unsuspending the workload will disable the "suspend" property.</p>
          </div>
        </Modal>
      ) : action === "forceRedeploy" ? (
        <Modal
          title={"Force Redeployment"}
          open={true}
          maskClosable={false}
          onCancel={() => setAction("")}
          onOk={async () => {
            setIsProcessingAction(true);
            const result = await workloadHelpers.handleForceRedeployment([selfLink]);
            setIsProcessingAction(false);
            if (result === true) {
              notification.success({
                message: "Success",
                description: "Redeployment initiated",
              });
              updateLastDeploymentTimeOnHubspot();
              refetch();
              setAction("");
            } else {
              setAction("");
              notification.warning({
                message: "Failed",
                description: result,
              });
            }
          }}
          cancelText={`Cancel`}
          okText={`Confirm`}
          cancelButtonProps={{ disabled: isFetching || isProcessingAction, type: "default", danger: true }}
          okButtonProps={{
            disabled: isFetching || isProcessingAction,
            loading: isFetching || isProcessingAction,
            type: "primary",
          }}
        >
          <div>Are you sure you want to force redeployment for this workload?</div>
        </Modal>
      ) : action === "tfImportCommand" ? (
        <TerraformImportModal command={terraformImportCommand} onClose={() => setAction("")} />
      ) : action === "k8sCrdExport" ? (
        <K8sCrdExportModal
          kind={kind}
          links={[selfLink]}
          onClose={() => {
            setAction("");
          }}
        />
      ) : null}
    </>
  );
};
